import React from 'react';
import { AlertColor, SxProps, Theme } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';

type SeverityColors = {
  background: string;
  color: string;
};

/**
 * Returns the color variables for a given severity level.
 * Uses the Nexla color scheme with -50 for a background and -700 for text/icons.
 */
function getSeverityColors(severity: AlertColor): SeverityColors {
  switch (severity) {
    case 'success':
      return {
        background: 'var(--color-success-50)',
        color: 'var(--color-success-700)',
      };
    case 'error':
      return {
        background: 'var(--color-danger-50)',
        color: 'var(--color-danger-700)',
      };
    case 'warning':
      return {
        background: 'var(--color-warning-50)',
        color: 'var(--color-warning-700)',
      };
    case 'info':
      return {
        background: 'var(--color-info-50)',
        color: 'var(--color-info-700)',
      };
    default:
      return {
        background: 'var(--color-greyscale-50)',
        color: 'var(--color-greyscale-700)',
      };
  }
}

/**
 * Returns the icon component for a given severity level.
 */
export function getSeverityIcon(severity: AlertColor): React.ReactElement {
  const iconColor = getSeverityColors(severity).color;

  switch (severity) {
    case 'success':
      return <CheckCircleIcon sx={{ fill: iconColor }} />;
    case 'error':
      return <ErrorIcon sx={{ fill: iconColor }} />;
    case 'warning':
      return <WarningIcon sx={{ fill: iconColor }} />;
    case 'info':
      return <InfoIcon sx={{ fill: iconColor }} />;
    default:
      return <InfoIcon sx={{ fill: iconColor }} />;
  }
}

/**
 * Returns the sx prop for the Alert component based on severity.
 * This matches the legacy ErrorDisplay styling pattern.
 */
export function getAlertSx(severity: AlertColor): SxProps<Theme> {
  const colors = getSeverityColors(severity);

  return {
    width: '100%',
    minWidth: 300,
    maxWidth: 500,
    borderRadius: '10px',
    boxShadow: 3,
    backgroundColor: colors.background,
    color: colors.color,
    // Ensure message text uses the correct color
    '& .MuiAlert-message': {
      color: 'inherit',
    },
  };
}

/**
 * Returns the sx prop for the close button.
 */
export const getCloseButtonSx: SxProps<Theme> = {
  color: 'var(--color-greyscale-700)',
  '&:hover': {
    backgroundColor: 'var(--color-greyscale-100)',
  },
};
